/**
 * Priority-based marker declutter for the observations map.
 *
 * When two station markers overlap on screen the higher-priority one stays
 * visible and the lower-priority one is hidden. Priority is the station
 * ordering already produced by `orderStationsByPriority` (lower number = higher
 * priority). The collision pass is disabled once the map is zoomed in past
 * {@link MARKER_COLLISION_MAX_ZOOM}, where markers are far enough apart that
 * hiding them only removes useful detail.
 */

/**
 * Zoom level at (and above) which collision hiding is disabled and every marker
 * is shown. Chosen so that at the Netherlands overview zoom levels markers
 * declutter, while zooming into a region reveals all stations again. Adjust if
 * the product wants a different cut-off.
 */
export const MARKER_COLLISION_MAX_ZOOM = 9;

export type MarkerBox = {
  id: string;
  /** Screen-space bounding box (e.g. from `getBoundingClientRect`). */
  left: number;
  top: number;
  right: number;
  bottom: number;
  /** Lower value = higher priority; kept first when boxes overlap. */
  priority: number;
  /** Never hidden and still reserves space (e.g. the selected station). */
  pinned?: boolean;
};

function boxesOverlap(a: MarkerBox, b: MarkerBox, padding: number): boolean {
  return (
    a.left < b.right + padding &&
    a.right + padding > b.left &&
    a.top < b.bottom + padding &&
    a.bottom + padding > b.top
  );
}

/**
 * Greedy declutter: walk markers from highest to lowest priority, keeping a
 * marker whenever its box does not overlap any already-kept box, and hiding it
 * otherwise. Pinned markers are always kept (and still occupy space, so
 * lower-priority markers overlapping them are hidden).
 *
 * @returns the set of marker ids that should be hidden.
 */
export function computeHiddenMarkerIds(
  boxes: MarkerBox[],
  options: { padding?: number } = {},
): Set<string> {
  const padding = options.padding ?? 0;
  const ordered = [...boxes].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    return a.priority - b.priority;
  });

  const kept: MarkerBox[] = [];
  const hidden = new Set<string>();

  for (const box of ordered) {
    const collides = kept.some((keptBox) => boxesOverlap(box, keptBox, padding));
    if (collides && !box.pinned) {
      hidden.add(box.id);
    } else {
      kept.push(box);
    }
  }

  return hidden;
}
